import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useMemo,
} from 'react';
import SockJS from 'sockjs-client';
import { Client } from '@stomp/stompjs';

const WebSocketContext = createContext(null);

export const useWebSocket = () => useContext(WebSocketContext);

export function WebSocketProvider({ loginId, children }) {
  const [connected, setConnected] = useState(false);
  const [duplicateLogin, setDuplicateLogin] = useState(false);
  const [deliveryMode, setDeliveryMode] = useState(
    () => localStorage.getItem('deliveryMode') || 'api'
  );
  const clientRef = useRef(null);

  // Memoize API_BASE_URL so it doesn't change on every render
  const API_BASE_URL = useMemo(() => {
    const ENV = process.env.REACT_APP_ENV;
    return ENV === 'dev'
      ? process.env.REACT_APP_API_BASE_URL
      : window.location.origin;
  }, []);

  // Persist delivery mode to localStorage
  useEffect(() => {
    localStorage.setItem('deliveryMode', deliveryMode);
  }, [deliveryMode]);

  useEffect(() => {
    // If user is not logged in OR delivery mode is 'api' -> ensure WS is closed
    if (!loginId || deliveryMode === 'api') {
      if (clientRef.current) {
        console.log('Disconnecting WebSocket (not logged in or API mode)');
        clientRef.current.deactivate();
        clientRef.current = null;
      }
      setConnected(false);
      setDuplicateLogin(false);
      return;
    }

    // Only connect if deliveryMode is 'socket'
    if (deliveryMode !== 'socket') {
      return;
    }

    console.log('Connecting WebSocket (Socket mode)');

    // Create STOMP client
    const client = new Client({
      webSocketFactory: () => new SockJS(`${API_BASE_URL}/radius`),
      connectHeaders: { login_id: loginId },
      debug: () => {},
      reconnectDelay: 5000,
    });

    client.onConnect = () => {
      setConnected(true);
      console.log('Websocket Connected');

      // Subscribe to login-status for this loginId
      client.subscribe(`/topic/login-status/${loginId}`, (msg) => {
        try {
          console.log(msg.body);
          const body = JSON.parse(msg.body);
          if (body.status === 'DUPLICATE_LOGIN') {
            setDuplicateLogin(true);
          }
        } catch (e) {
          console.error('Failed to parse login-status message', e);
        }
      });
    };

    client.onStompError = (frame) => {
      console.error('Broker reported STOMP error:', frame.headers['message']);
      console.error('Additional details:', frame.body);
      setConnected(false);
    };

    client.onWebSocketError = (error) => {
      console.error('WebSocket error', error);
      setConnected(false);
    };

    clientRef.current = client;
    client.activate();

    // Cleanup on unmount / loginId change / deliveryMode change
    return () => {
      setConnected(false);
      setDuplicateLogin(false);
      if (clientRef.current) {
        console.log('Cleaning up WebSocket connection');
        clientRef.current.deactivate();
        clientRef.current = null;
      }
    };
  }, [loginId, deliveryMode, API_BASE_URL]);

  const send = (destination, body, headers = {}) => {
    const client = clientRef.current;
    if (!client || !client.connected) return;

    client.publish({
      destination,
      headers,
      body: JSON.stringify(body),
    });
  };

  const value = useMemo(
    () => ({
      connected,
      duplicateLogin,
      deliveryMode,
      setDeliveryMode,
      clearDuplicateFlag: () => setDuplicateLogin(false),
      send,
      client: clientRef.current,
    }),
    [connected, duplicateLogin, deliveryMode]
  );

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
}